import { createContext, useState, useEffect } from "react";
import { setDatadogUser, clearDatadogUser } from "@/services/datadog";
import { Api } from "@/services";
import { showToast } from "@/utils/toast";
import { useNavigate } from "react-router-dom";
import { useFeatureToggle, useSettings } from "@/hooks";
import { v4 as uuidv1 } from 'uuid';
import { Api_Banking } from "@/services/Api/bankingApiConfig";
import { getTenantConfig } from "@/config/tenantConfig";
import { resolvePostLoginRedirect, storeReturnUrl } from "@/utils/postLoginRedirect";

type signInDataType = {
  email: string;
  password: string;
};

type signInDataTwoFa = {
  type?: string;
  twofaToken: any;
  pin: string;
}
type UserType = {
  email: string;
  roles: string[];
  idSeller: number;
  document: string;
  companyName: string;
  fantasyName: string;
  companyId?: number;
};

type AuthContextType = {
  isAuthenticated: boolean;
  user: UserType;
  signIn: (data: signInDataType) => Promise<UserType | null>;
  signInTwoFactor: (data: signInDataTwoFa) => Promise<void>;
  featureTogglesUser: string[];
  getFeatureTogglesList: () => void;
};

export const AuthContext = createContext({} as AuthContextType);

function resolveFallbackPath(roles?: string[]): string {
  if (!roles?.includes("SELLER_DASHBOARD")) return "/cobrancas";
  if (roles?.includes("SELLER_BUSINESS")) return "/seja-pago";
  return "/";
}

export function isImpersonating(): boolean {
  return Boolean(localStorage.getItem("barte-impersonation"));
}

export function signOut(captureReturnUrl = false) {
  const impersonating = isImpersonating();
  clearDatadogUser();
  localStorage.removeItem("barte-auth-token");
  localStorage.removeItem("barte-user-info");
  localStorage.removeItem("barte-features-list-user");
  localStorage.removeItem("chargeTableFilters");
  localStorage.removeItem("barte-impersonation");
  localStorage.removeItem("barte-impersonator");
  localStorage.removeItem("barte-impersonation-exp");
  localStorage.removeItem("barte-return-url");
  // Reseta o flag para que o próximo usuário tenha os feature toggles atualizados
  window.__isFirstLoad = false;
  if (captureReturnUrl && !impersonating) {
    storeReturnUrl(window.location.pathname + window.location.search);
  }
  window.location.replace("/login");
}
const getUser = () => {
  if (typeof window !== "undefined") {
    const user = JSON.parse(localStorage.getItem("barte-user-info"));
    if (!user) return null;
    return user;
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState < UserType | null > (getUser);
  const [featureTogglesUser, setFeatureTogglesUser] = useState(null);
  const isAuthenticated = false;
  const navigate = useNavigate();
  const { getFeatureToggles } = useFeatureToggle();
  const [attemptReference] = useState(uuidv1());
  const approvalPSPBanking = ["barte", "cashfast"]
  async function getFeatureTogglesList(hasBankingAccount?: boolean, blockItemsCreditCard?: boolean) {
    try {
      const featureToggleResponse = await getFeatureToggles();
      setFeatureTogglesUser(featureToggleResponse.data);
      const updatedFeatureToggles = [...featureToggleResponse.data];
      localStorage.removeItem("barte-features-list-user");
      if(featureToggleResponse.data.includes("disable_paymentlink")){
        const index = updatedFeatureToggles.findIndex(feature => feature === "view_seller_payment_link");
        if (index !== -1) {
          updatedFeatureToggles.splice(index, 1);
          localStorage.setItem(
            "barte-features-list-user",
            JSON.stringify(updatedFeatureToggles)
          );
        }
      }
      if (hasBankingAccount) {
        const index = updatedFeatureToggles.findIndex(feature => feature === "view_create_banking_account");
        if (index !== -1) {
          updatedFeatureToggles.splice(index, 1);
          localStorage.setItem(
            "barte-features-list-user",
            JSON.stringify(updatedFeatureToggles)
          );
        }
        localStorage.setItem(
          "barte-features-list-user",
          JSON.stringify(updatedFeatureToggles)
        );
        if (blockItemsCreditCard) {
          updatedFeatureToggles.push("block_items_credit_card");
          localStorage.setItem(
            "barte-features-list-user",
            JSON.stringify(updatedFeatureToggles)
          );
        }
      } else {
        localStorage.setItem(
          "barte-features-list-user",
          JSON.stringify(updatedFeatureToggles)
        );
      }

    } catch (error) {
      return error
    }
  }

  useEffect(() => {
    if (attemptReference) {
      const nethoneScript = document.createElement("script");
      nethoneScript.type = "text/javascript";
      nethoneScript.id = "nethone-script";
      nethoneScript.crossOrigin = "use-credentials";
      nethoneScript.src = "https://d354c9v5bptm0r.cloudfront.net/s/68741/dQItJr.js";
      nethoneScript.async = true
      document.body.appendChild(nethoneScript);
      nethoneScript.onload = () => {
        localStorage.setItem("attempt_reference", attemptReference)
        const nethoneOptions = {
          attemptReference: attemptReference,
          sensitiveFields: ["cardHolderName", "cardCvv", "cardExpirationDate", "cardNumber"],
        };

        if ((window as any).dftp) {
          (window as any).dftp.init(nethoneOptions);
        } else {
          nethoneScript.addEventListener("load", () => {
            if ((window as any).dftp) {
              (window as any).dftp.init(nethoneOptions);
            }
          });
        }
      }
    };
  }, [attemptReference]);


  async function signIn(data: signInDataType) {
    localStorage.removeItem("barte-auth-token");
    const newData = { ...data, attemptReference }
    try {
      const login = await Api.post("public/auth/sign-in", newData);
      const { email, roles, bankingRoles, token, refreshToken, idSeller, mixedInstallment, document, isActiveForTax, bankingType, companyName, fantasyName, companyId } = login.data;
      const updatedBankingRoles = [...bankingRoles]
      const findIndex = updatedBankingRoles.findIndex(br => br === "SELLER_ADMIN");
      if(findIndex !== -1) {
        updatedBankingRoles[findIndex] = "B_SELLER_ADMIN";
      }
      const formattedRoles = [...roles, ...updatedBankingRoles]
      localStorage.setItem("barte-auth-token", token);
      localStorage.setItem("barte-refresh-token", refreshToken);
      // localStorage.setItem("barte-user-info", JSON.stringify({ email, roles, idSeller, document, bankingType, companyName, fantasyName }));
      localStorage.setItem("barte-user-document", document);
      localStorage.setItem("isActiveForTax", String(isActiveForTax));
      localStorage.setItem("hasMixInstallments", String(mixedInstallment))
      Api.defaults.headers["Authorization"] = `Bearer ${token}`;
      Api_Banking.defaults.headers["Authorization"] = `Bearer ${token}`;
      setUser({ email, roles, idSeller, document, companyName, fantasyName, companyId });
      setDatadogUser({ idSeller, companyId });

      if (roles.includes("TWO_FACTOR_AUTH")) {
        return { email, roles, idSeller, document, token, refreshToken, companyName, fantasyName }
      }

      if(roles?.includes("SELLER_ADMIN") && approvalPSPBanking.includes(getTenantConfig().applicationName)){
        // Liberar tela de cadastro de conta para user backoffice
        const updatedBankingRoles = bankingRoles.filter((br: string) => br !== "SELLER_ADMIN")
        await getFeatureTogglesList(updatedBankingRoles?.length > 0, false);
        //Mantem a verificação de status da conta com roles de banking
        localStorage.setItem("barte-user-info", JSON.stringify({ email, roles: formattedRoles, idSeller, document, bankingType, companyName, fantasyName, companyId, bankingStatus: bankingRoles?.length > 0 ? "ACTIVE" : "INACTIVE" }));
      } else {
        await getFeatureTogglesList(false, false);
        localStorage.setItem("barte-user-info", JSON.stringify({ email, roles: formattedRoles, idSeller, document, bankingType, companyName, fantasyName, companyId, bankingStatus: "INACTIVE" }));
      }
      
      /* @ts-ignore */
      if (window?.userGuiding) {
        //@ts-ignore
        window?.userGuiding?.identify(email, {
          name: email,
          email: email,
          created_at: new Date(),
        })
      }
      // Navega e depois força reload para garantir remount dos componentes.
      // Se o usuário veio de uma rota protegida (ex.: link de fatura), volta pra ela.
      window.location.href = resolvePostLoginRedirect(resolveFallbackPath(roles));
    } catch (error) {
      error?.response?.data?.errors.map((error) => {
        if (error?.code === "BAR-9999") return showToast({ message: "Erro ao realizar login", type: "error" });
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      });
    }
  }

  async function signInTwoFactor({ twofaToken, pin, type }: signInDataTwoFa) {
    try {
      const login = await Api.post(
        `twofactor/verify?code=${pin}&loginType=${type}`,
        {},
        {
          headers: {
            "Authorization": `Bearer ${twofaToken}`,
            "Content-Type": "application/json"
          }
        }
      );
      const { email, roles, bankingRoles, token, refreshToken, idSeller, mixedInstallment, document, companyName, fantasyName, companyId } = login.data;


      const updatedBankingRoles = [...bankingRoles]
      const findIndex = updatedBankingRoles.findIndex(br => br === "SELLER_ADMIN");
      if(findIndex !== -1) {
        updatedBankingRoles[findIndex] = "B_SELLER_ADMIN";
      }
      const formattedRoles = [...roles, ...updatedBankingRoles]
      
      localStorage.setItem("barte-auth-token", token);
      localStorage.setItem("barte-refresh-token", refreshToken);
      localStorage.setItem("barte-user-document", document);
      localStorage.setItem("hasMixInstallments", String(mixedInstallment))
      Api.defaults.headers["Authorization"] = `Bearer ${token}`;
      Api_Banking.defaults.headers["Authorization"] = `Bearer ${token}`;
      setUser({ email, roles, idSeller, document, companyName, fantasyName, companyId });
      setDatadogUser({ idSeller, companyId });
      if(roles?.includes("SELLER_ADMIN")  && approvalPSPBanking.includes(getTenantConfig().applicationName)){
        // Liberar tela de cadastro de conta para user backoffice
        const updatedBankingRoles = bankingRoles.filter((br: string) => br !== "SELLER_ADMIN")
        await getFeatureTogglesList(updatedBankingRoles?.length > 0, false);
        //Mantem a verificação de status da conta com roles de banking
        localStorage.setItem("barte-user-info", JSON.stringify({ email, roles: formattedRoles, idSeller, document, companyName, fantasyName, companyId, bankingStatus: bankingRoles?.length > 0 ? "ACTIVE" : "INACTIVE" }));
      } else {
        await getFeatureTogglesList(false, false);
        localStorage.setItem("barte-user-info", JSON.stringify({ email, roles: formattedRoles, idSeller, document, companyName, fantasyName, companyId, bankingStatus: "INACTIVE" }));
      }

      /* @ts-ignore */
      if (window?.userGuiding) {
        //@ts-ignore
        window?.userGuiding?.identify(email, {
          name: email,
          email: email,
          created_at: new Date(),
        })
      }
      // Navega e depois força reload para garantir remount dos componentes.
      // Se o usuário veio de uma rota protegida (ex.: link de fatura), volta pra ela.
      window.location.href = resolvePostLoginRedirect(resolveFallbackPath(roles));

    } catch (error) {
      error?.response?.data?.errors?.forEach((error) => {
        if (error?.code === "BAR-9999") return showToast({ message: "Erro ao realizar login", type: "error" });
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`
        })
      });
    }
  }

  useEffect(() => {
    if (user) setDatadogUser(user);
  }, []);

  useEffect(() => {
    try {
      const featuresListData = localStorage.getItem("barte-features-list-user");
      if (featuresListData) {
        const featuresList = JSON.parse(featuresListData);
        if (featuresList && featuresList.length > 0) {
          setFeatureTogglesUser(featuresList);
        }
      }
    } catch (error) {
      console.error("Error parsing features list from localStorage:", error);
      localStorage.removeItem("barte-features-list-user");
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        signIn,
        featureTogglesUser,
        getFeatureTogglesList,
        signInTwoFactor,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
